package com.neurobs.js.ccab

import com.neurobs.ccab.{BasicMessage, CaasType, ChatClientId, MessageData, MessageType, SplitMessageReceiver}
import com.neurobs.js.dom.VideoElement
import com.neurobs.util.{ChatConnectionStatus, ChatSession, ChatSessionFactory, ChatSessionNotify, CommunicationState, Logger}
import com.neurobs.util.JsonPrinter.jsonPrint
import io.circe.Encoder
import io.circe.generic.auto._

import scala.collection.mutable.ArrayBuffer

object ControllerConnectionStatus extends Enumeration {
	type Status = Value
	val Connecting = Value
	val Connected = Value
	val Disconnected = Value
	val Closed = Value
}

trait CCABChatSessionNotify {
	/** Called when the connection to Test Station changes
	 *
	 * @param newStatus New status
	 * @param error Error message if available
	 */
	def connectionStatusChanged(newStatus: ControllerConnectionStatus.Value, error: Option[String]): Unit

	/** Called when the communication state changes
	 *
	 * @param newState The new state
	 */
	def communicationStateChanged(newState: CommunicationState): Unit

	/** Called when there is an error starting AV transmission
	 * to other clients
	 */
	def publishAVError(error: String): Unit

	/** Called when there is an error subscribing to a remote stream
	 */
	def subscribeToRemoteError(error: String): Unit

	/** Called  for other errors */
	def genericError(error: String): Unit

	def controllerMessageReceived(message: MessageData): Unit

	def examinerListChanged(examiners: Seq[String]): Unit

	def examinerChat(message: String, name: String): Unit
}

class CCABChatSession private (session: ChatSession, adapter: CCABChatSession.CCABChatSessionAdapter) {
	import MessageData._
	import CCABChatSession._

	def apiType: CaasType.Value = session.apiType

	def examinerNames: Seq[String] = adapter.examiners

	def connectionStatus: ControllerConnectionStatus.Value = adapter.status

	def send[T <: MessageData](msg: T)(implicit encoder: Encoder[T]): Unit = {
		sendAdminMessage(msg = jsonPrint(msg))
	}

	def send(t: MessageType.Value): Unit = {
		send(BasicMessage(`type` = t))
	}

	def sendAdminMessage(msg: String): Unit = {
		session.sendMessage(message = msg, messageType = adminMessageTypeString)
	}

	def sendAdminChat(message: String): Unit = {
		session.sendMessage(message = message, messageType = adminChatMessageTypeString)
	}

	def isPublishingAV: Boolean = session.isPublishingAV

	def setPublishing(audio: Boolean, video: Boolean): Unit = session.setPublishing(audio = audio, video = video)

	def close(): Unit = session.close()

	def setPlaybackVolume(volume: Double): Unit = session.setPlaybackVolume(volume)
}

object CCABChatSession {
	val adminMessageTypeString = "Admin"
	val adminChatMessageTypeString = "AdminChat"
	val testStationMessageTypeString = "Controller"
	val testStationClientTypeString = "t"
	val examinerClientTypeString = "e"

	private class CCABChatSessionAdapter(notify: CCABChatSessionNotify)(implicit logger: Logger) extends ChatSessionNotify {
		private var connectedOnce = false
		private var connectionStatus = ChatConnectionStatus.Connecting
		private var controllerConnected = false
		private val splitMessageReceiver = new SplitMessageReceiver
		val examiners = ArrayBuffer[String]()

		def status: ControllerConnectionStatus.Value = connectionStatus match {
			case ChatConnectionStatus.Connecting => ControllerConnectionStatus.Connecting
			case ChatConnectionStatus.Disconnected => ControllerConnectionStatus.Disconnected
			case ChatConnectionStatus.Closed => ControllerConnectionStatus.Closed
			case ChatConnectionStatus.Connected =>
				if (controllerConnected) ControllerConnectionStatus.Connected
				else if(!connectedOnce) ControllerConnectionStatus.Connecting
				else ControllerConnectionStatus.Disconnected
		}

		private def updateStatus(newConnectionStatus: ChatConnectionStatus.Value, newControllerConnected: Boolean, error: Option[String]): Unit = {
			val before = status
			connectionStatus = newConnectionStatus
			controllerConnected = newControllerConnected
			val after = status

			if(after == ControllerConnectionStatus.Connected)
				connectedOnce = true
			if (after != before) notify.connectionStatusChanged(after, error)
		}

		override def connectionStatusChanged(newStatus: ChatConnectionStatus.Value, error: Option[String]): Unit = {
			updateStatus(newStatus, controllerConnected, error)
		}

		override def communicationStateChanged(newState: CommunicationState): Unit = notify.communicationStateChanged(newState)

		override def publishAVError(error: String): Unit = notify.publishAVError(error)

		override def subscribeToRemoteError(error: String): Unit = notify.subscribeToRemoteError(error)

		override def genericError(error: String): Unit = notify.genericError(error)

		override def remoteClientConnected(id: ChatClientId): Unit = {
			if (id.`type` == testStationClientTypeString) {
				updateStatus(connectionStatus, newControllerConnected = true, None)
			}
			else if (id.`type` == examinerClientTypeString) {
				examiners.append(id.user)
				notify.examinerListChanged(examiners)
			}
		}

		override def remoteClientDisconnected(id: ChatClientId): Unit = {
			if (id.`type` == testStationClientTypeString) {
				updateStatus(connectionStatus, newControllerConnected = false, Some("Test Station disconnected from the chat session."))
			}
			else if (id.`type` == examinerClientTypeString) {
				examiners -= id.user
				notify.examinerListChanged(examiners)
			}
		}

		override def messageReceived(message: String, messageType: String, source: String): Unit = {
			if (messageType == testStationMessageTypeString) {
				splitMessageReceiver.process(message) map { m =>
					MessageData.decode(m) match {
						case None => logger.debug(s"CCAB unrecognized message '$m'.")
						case Some(data) => notify.controllerMessageReceived(data)
					}
				}
			}
			else if (messageType == adminChatMessageTypeString) {
				notify.examinerChat(message, source)
			}
		}
	}

	def connect(factory: ChatSessionFactory, thisClient: ChatClientId, notify: CCABChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement)(implicit logger: Logger): Either[String, CCABChatSession] = {
		val adapter = new CCABChatSessionAdapter(notify)
		factory.connect(thisClient, adapter, localVideoElement = localVideoElement, remoteVideoElement = remoteVideoElement, remoteAdminVideoElement = remoteAdminVideoElement).map(
		new CCABChatSession(_, adapter))
	}
}